import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Grid } from 'antd';
import { useRegions } from '@hooks/useRegions/useRegions';
import { CalculatorCurrencyField } from '@components/common/CalculatorCurrencyField/CalculatorCurrencyField';
import { CalculatorDoubleSelect } from '@components/common/CalculatorDoubleSelect/CalculatorDoubleSelect';
import { CalculatorNumberField } from '@components/common/CalculatorNumberField/CalculatorNumberField';
import { CalculatorSelect } from '@components/common/CalculatorSelect/CalculatorSelect';
import { CalculatorSubmitButton } from '@components/common/CalculatorSubmitButton/CalculatorSubmitButton';
import { CalculatorFormControl } from '@components/common/CalculatorFormControl/CalculatorFormControl';
import { generateArrayOfNumbers } from '@utils/numbers';
import { scrollToTop } from '@utils/general';
import { saveAffordabilityFormToLocalStorage, setLastAffordabilityCalcPage } from '@utils/localStorage';
import { CalculatorLayout } from '@pages/CalculatorPage/CalculatorLayout/CalculatorLayout';
import { applicantDefaultValues } from '../helpers';
import { AffordabilityCalculatorForm, CalculatorPages, IProductSelectOption } from '../types';
import { FinanceDetailsQuestions } from './questions';
import {
  countFtvValue,
  extractFinancingAmountUpperLimitByFtv,
  getDefaultMaxFinanceAmount,
  updateFinancingAmountErrorOnFtvChange,
} from './helpers';
import { MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO } from '../criteria';

const { useBreakpoint } = Grid;

export const FinanceDetailsPage = () => {
  const intl = useIntl();
  const screens = useBreakpoint();
  const isMobileView = !screens.md;
  const form = useFormContext<AffordabilityCalculatorForm>();
  const { regionsOptions } = useRegions();

  const [searchParams, setSearchParams] = useSearchParams();
  const [financeAmountUpperLimit, setFinanceAmountUpperLimit] = useState(getDefaultMaxFinanceAmount());

  const {
    financeAmount,
    deposit: depositQuestions,
    ftv: ftvQuestions,
    product,
    term,
    numberOfApplicants,
    region,
    dependants,
    error,
  } = FinanceDetailsQuestions;

  const formFields = form.watch();

  const yearsOptions = generateArrayOfNumbers(40, 5).map(year => ({ value: year, label: String(year) }));
  const monthsOptions = generateArrayOfNumbers(11, 0).map(month => ({ value: month, label: String(month) }));

  const deposit = formFields.financeDetails.deposit;
  const financingAmount = formFields.financeDetails.financingAmount;
  const years = formFields.financeDetails.term.years;

  const goToNextPage = async () => {
    const triggerResultPromise = form.trigger('financeDetails', { shouldFocus: true });
    const isNoErrors = await triggerResultPromise;

    if (isNoErrors) {
      const desiredNumberOfApplicants = Number(formFields.financeDetails.numberOfApplicants) || 0;
      const existingNumberOfApplicants = formFields.applicants.length;

      if (desiredNumberOfApplicants < existingNumberOfApplicants && desiredNumberOfApplicants > 0) {
        searchParams.set('applicantNumber', String(desiredNumberOfApplicants - 1));
        setSearchParams(searchParams);
      }

      const newApplicantsArray = [];
      for (let i = 0; i < desiredNumberOfApplicants; i++) {
        if (i < existingNumberOfApplicants) {
          newApplicantsArray.push(formFields.applicants[i]);
        } else newApplicantsArray.push(applicantDefaultValues);
      }
      form.setValue('applicants', newApplicantsArray);

      const updatedFormFields = form.getValues();

      saveAffordabilityFormToLocalStorage(updatedFormFields);
      setLastAffordabilityCalcPage(CalculatorPages.applicantDetails);
      searchParams.set('section', CalculatorPages.applicantDetails);
      setSearchParams(searchParams);
      scrollToTop();
    }
  };

  useEffect(() => {
    if (years !== null && years === 40) {
      form.setValue('financeDetails.term.months', 0);
      form.trigger('financeDetails.term.months');
    }
  }, [years]);

  useEffect(() => {
    const ftv = countFtvValue(deposit || 0, financingAmount || 0);

    if (ftv) {
      const updatedFinanceAmountUpperLimit = extractFinancingAmountUpperLimitByFtv(ftv);

      if (updatedFinanceAmountUpperLimit !== financeAmountUpperLimit) {
        setFinanceAmountUpperLimit(updatedFinanceAmountUpperLimit);
      }

      updateFinancingAmountErrorOnFtvChange(ftv, financingAmount, { form, intl });
    }

    form.setValue('financeDetails.ftv', ftv);
  }, [deposit, financingAmount]);

  return (
    <CalculatorLayout currentSectionNumber={1} additionalClasses={'after:w-1/5'}>
      <CalculatorCurrencyField
        className={'xs:mb-5 md:mb-10'}
        rules={{
          required: intl.formatMessage({ id: error.required }),
          validate: (value: string) => {
            const currentDeposit = Number(value);

            if (financingAmount && currentDeposit) {
              const propertyPrice = financingAmount + currentDeposit;

              if (currentDeposit < propertyPrice * MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO)
                return intl.formatMessage(
                  { id: error.minimumDeposit },
                  { minDepositPercentage: `${MIN_DEPOSIT_TO_PROPERTY_PRICE_RATIO * 100}%` },
                );
            }
          },
        }}
        name={'financeDetails.deposit'}
        textKey={depositQuestions.textKey}
        titleKey={depositQuestions.titleKey}
      />
      <CalculatorCurrencyField
        className={'xs:mb-5 md:mb-10'}
        rules={{
          validate: (value: string) => {
            if (value === null || value === '') return true;
            if (Number(value) < financeAmount.min) return intl.formatMessage({ id: error.min });
            if (Number(value) > financeAmountUpperLimit) return intl.formatMessage({ id: error.max });
          },
        }}
        name={'financeDetails.financingAmount'}
        textKey={financeAmount.textKey}
        titleKey={financeAmount.titleKey}
      />
      <CalculatorNumberField
        className={'xs:mb-5 xs:w-full md:mb-10 md:w-1/2'}
        name={'financeDetails.ftv'}
        textKey={ftvQuestions.textKey}
        titleKey={ftvQuestions.titleKey}
        textFieldProps={{ disabled: true, suffix: '%' }}
        numericFormatProps={{ decimalScale: 1 }}
      />
      <CalculatorSelect
        name={'financeDetails.product'}
        className={'xs:mb-10 md:mb-12'}
        options={product.options}
        titleKey={product.titleKey}
        textKey={product.textKey}
        placeholderKey={product.placeholderKey}
        selectProps={{
          groupBy: (option: IProductSelectOption) => option.productType,
          disableClearable: false,
        }}
      />
      <hr className={'border-fog xs:mb-10 border border-t-0 border-solid md:mb-12'} />
      <CalculatorDoubleSelect
        className={'xs:mb-5 md:mb-10'}
        names={['financeDetails.term.years', 'financeDetails.term.months']}
        textKey={term.textKey}
        titleKey={term.titleKey}
        rules={{ required: intl.formatMessage({ id: error.required }) }}
        options={[yearsOptions, monthsOptions]}
        isDisabled={[false, years === 40]}
        placeholdersKeys={term.placeholdersKeys}
      />
      <CalculatorFormControl
        className={'xs:mb-5 md:mb-10'}
        name={'financeDetails.numberOfApplicants'}
        titleKey={numberOfApplicants.titleKey}
        textKey={numberOfApplicants.textKey}
        rules={{ required: intl.formatMessage({ id: error.required }) }}
        radioOptions={numberOfApplicants.options}
        isWide={isMobileView}
      />
      <CalculatorSelect
        name={'financeDetails.region'}
        className={'xs:mb-5 md:mb-10'}
        options={regionsOptions}
        titleKey={region.titleKey}
        textKey={region.textKey}
        rules={{ required: intl.formatMessage({ id: error.required }) }}
        placeholderKey={region.placeholderKey}
      />
      <CalculatorFormControl
        className={'mb-10'}
        name={'financeDetails.dependents'}
        titleKey={dependants.titleKey}
        textKey={dependants.textKey}
        rules={{ required: intl.formatMessage({ id: error.required }) }}
        radioOptions={dependants.options}
        isWide
      />
      <div className={'xs:mb-10 xs:justify-center flex items-center md:justify-end'}>
        <CalculatorSubmitButton onClick={goToNextPage} />
      </div>
    </CalculatorLayout>
  );
};
